import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, Tooltip, Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import { bloomberg } from '../theme/bloombergTheme';
import { useAuth } from '../contexts/AuthContext';
import {
  AdminUser, adminListUsers, adminCreateUser, adminDeleteUser, adminUpdateUser,
} from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// AdminPortal — admin-only user management: list, add, delete, toggle active,
// change role, reset password. Route is guarded to admins in App.tsx.
// ──────────────────────────────────────────────────────────────────────────

const fmtDate = (s: string | null) =>
  s ? new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const AdminPortal: React.FC = () => {
  const { username: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-user dialog
  const [addOpen, setAddOpen] = useState(false);
  const [nu, setNu] = useState({ username: '', password: '', email: '', role: 'user' });
  const [adding, setAdding] = useState(false);

  // Reset-password dialog
  const [pwTarget, setPwTarget] = useState<AdminUser | null>(null);
  const [newPw, setNewPw] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await adminListUsers());
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAdd = async () => {
    setAdding(true); setError(null);
    try {
      await adminCreateUser(nu);
      setAddOpen(false);
      setNu({ username: '', password: '', email: '', role: 'user' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to create user');
    } finally {
      setAdding(false);
    }
  };

  const doDelete = async (u: AdminUser) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await adminDeleteUser(u.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const doToggle = async (u: AdminUser) => {
    try {
      await adminUpdateUser(u.id, { is_active: !u.is_active });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update user');
    }
  };

  const doRole = async (u: AdminUser, role: string) => {
    try {
      await adminUpdateUser(u.id, { role });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to change role');
    }
  };

  const doResetPw = async () => {
    if (!pwTarget || newPw.length < 6) return;
    try {
      await adminUpdateUser(pwTarget.id, { password: newPw });
      setPwTarget(null); setNewPw('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to reset password');
    }
  };

  const th: React.CSSProperties = {
    padding: '8px 10px', fontSize: 10, fontWeight: 700, textAlign: 'left',
    color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: `1px solid ${bloomberg.border.primary}`,
  };
  const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13, color: bloomberg.text.secondary, borderBottom: `1px solid ${bloomberg.border.primary}` };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>User Administration</Typography>
          <Typography sx={{ fontSize: 13, color: bloomberg.text.muted }}>
            {users.length} registered {users.length === 1 ? 'user' : 'users'} · {users.filter(u => u.is_active).length} active
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: bloomberg.text.muted }}><RefreshIcon /></IconButton></Tooltip>
          <Button startIcon={<PersonAddIcon />} variant="contained" onClick={() => setAddOpen(true)}
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#ffa733' } }}>
            Add user
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>User</th>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Status</th>
                <th style={th}>Created</th>
                <th style={th}>Last login</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ ...td, fontWeight: 600, color: bloomberg.text.primary, fontFamily: '"IBM Plex Mono", monospace' }}>
                    {u.username}{u.username === me && <Chip label="you" size="small" sx={{ ml: 1, height: 16, fontSize: 9 }} />}
                  </td>
                  <td style={td}>{u.email || '—'}</td>
                  <td style={td}>
                    <TextField select value={u.role} size="small" variant="standard"
                      onChange={e => doRole(u, e.target.value)} disabled={u.username === me}
                      sx={{ minWidth: 80, '& .MuiInput-input': { fontSize: 12 } }}>
                      <MenuItem value="user" sx={{ fontSize: 12 }}>user</MenuItem>
                      <MenuItem value="admin" sx={{ fontSize: 12 }}>admin</MenuItem>
                    </TextField>
                  </td>
                  <td style={td}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch size="small" checked={u.is_active} onChange={() => doToggle(u)} disabled={u.username === me} />
                      <Chip label={u.is_active ? 'active' : 'disabled'} size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: u.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.12)',
                          color: u.is_active ? '#22c55e' : '#ef4444' }} />
                    </Box>
                  </td>
                  <td style={{ ...td, fontSize: 11, color: bloomberg.text.muted }}>{fmtDate(u.created_at)}</td>
                  <td style={{ ...td, fontSize: 11, color: bloomberg.text.muted }}>{fmtDate(u.last_login)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <Tooltip title="Reset password"><IconButton size="small" onClick={() => { setPwTarget(u); setNewPw(''); }} sx={{ color: bloomberg.text.muted }}><KeyIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={u.username === me ? "Can't delete yourself" : 'Delete user'}>
                      <span><IconButton size="small" onClick={() => doDelete(u)} disabled={u.username === me} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Paper>

      {/* Add user dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} PaperProps={{ sx: { bgcolor: bloomberg.bg.secondary, minWidth: 380 } }}>
        <DialogTitle sx={{ fontSize: 18 }}>Add new user</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Username" size="small" value={nu.username} onChange={e => setNu({ ...nu, username: e.target.value })} autoFocus />
          <TextField label="Email (optional)" size="small" value={nu.email} onChange={e => setNu({ ...nu, email: e.target.value })} />
          <TextField label="Password" type="password" size="small" value={nu.password} onChange={e => setNu({ ...nu, password: e.target.value })}
            helperText="Minimum 6 characters" />
          <TextField select label="Role" size="small" value={nu.role} onChange={e => setNu({ ...nu, role: e.target.value })}>
            <MenuItem value="user">user</MenuItem>
            <MenuItem value="admin">admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: bloomberg.text.muted }}>Cancel</Button>
          <Button onClick={doAdd} variant="contained" disabled={adding || !nu.username || nu.password.length < 6}
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#ffa733' } }}>
            {adding ? <CircularProgress size={18} sx={{ color: '#000' }} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onClose={() => setPwTarget(null)} PaperProps={{ sx: { bgcolor: bloomberg.bg.secondary, minWidth: 360 } }}>
        <DialogTitle sx={{ fontSize: 18 }}>Reset password — {pwTarget?.username}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField label="New password" type="password" size="small" fullWidth value={newPw}
            onChange={e => setNewPw(e.target.value)} helperText="Minimum 6 characters" autoFocus sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwTarget(null)} sx={{ color: bloomberg.text.muted }}>Cancel</Button>
          <Button onClick={doResetPw} variant="contained" disabled={newPw.length < 6}
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#ffa733' } }}>
            Set password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPortal;
